import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { MailWarning, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/** Shown at the top of protected student pages when email is not verified.
 *  Admin bypasses this in AppShell.
 */
export function VerificationBanner() {
  const { user, refresh } = useAuth();
  const [sending, setSending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  if (!user || user.role === "admin" || user.email_verified) return null;

  const resend = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      toast.success(data.message || "Doğrulama e-postası gönderildi");
      // start 60s local cooldown
      setCooldown(60);
      const iv = setInterval(() => {
        setCooldown((c) => {
          if (c <= 1) { clearInterval(iv); return 0; }
          return c - 1;
        });
      }, 1000);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      data-testid="verification-banner"
      role="alert"
      className="border-b border-amber-200 bg-amber-50 px-4 lg:px-8 py-3 flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4"
    >
      <div className="flex items-start gap-2.5 flex-1 min-w-0">
        <div className="w-7 h-7 rounded-full bg-amber-100 border border-amber-200 flex items-center justify-center shrink-0">
          <MailWarning className="w-4 h-4 text-amber-700" />
        </div>
        <div className="text-sm text-amber-900 leading-snug">
          <span className="font-semibold">E-postanı doğrula.</span>{" "}
          <span className="text-amber-800">
            <strong className="font-semibold">{user.email}</strong> adresine gönderdiğimiz bağlantıya tıkla — sohbete başlayabilmen için gerekli.
          </span>
        </div>
      </div>
      <Button
        onClick={resend}
        disabled={sending || cooldown > 0}
        variant="outline"
        size="sm"
        className="rounded-full border-amber-300 bg-white text-amber-900 hover:bg-amber-100 h-8 text-xs shrink-0"
        data-testid="verification-resend"
      >
        {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
          cooldown > 0 ? `Tekrar (${cooldown}s)` : "Yeniden gönder"}
      </Button>
    </div>
  );
}
